import { useEffect, useRef, useState } from "react";
import { usePageMeta } from "../usePageMeta";
import { EmailLinkForm } from "../components/EmailLinkForm";
import { WITH_COOKIE } from "../tokenStorage";

const PAGE_TITLE = "Log in | Embroidery Online";
const PAGE_DESCRIPTION =
  "Log in to Embroidery Online with Google or an emailed link to get your saved designs back on any device.";

/** What /api/login/google and /api/login/email/verify both answer with. */
export interface LoginResult {
  token: string;
  email: string | null;
  avatar_url: string | null;
}

export type LoginMethod = "google" | "email";

interface LoginPageProps {
  apiUrl: string;
  /** This browser's current token, if any: an anonymous one is kept, under
   *  the verified email, when no other account already has that email. */
  token: string;
  /** False until App has settled which token this browser has, so a magic
   *  link isn't spent before the token it should attach to is known. */
  tokenResolved: boolean;
  /** The address this browser is logged in as, or "" when anonymous. */
  currentEmail: string;
  onLoggedIn: (result: LoginResult, method: LoginMethod) => void;
  /** Shown in place of the login options once logged in: logging in again
   *  from here would only switch accounts, which is not what someone
   *  landing on /login while logged in is usually after. */
  onLogOut: () => void;
}

/** The 409 /api/login/* answers when a logged-in browser would end up in a
 *  different account (`_confirm_switch` in app/routers/login.py). */
interface SwitchAccount {
  from_email: string | null;
  to_email: string;
  new_account: boolean;
}

// Google Identity Services, loaded from Google at runtime rather than
// bundled. Only the two calls used here are typed.
interface GoogleIdentity {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: { credential: string }) => void;
      }) => void;
      renderButton: (parent: HTMLElement, options: Record<string, string | number>) => void;
    };
  };
}

const GSI_SRC = "https://accounts.google.com/gsi/client";

function loadGoogleIdentity(): Promise<GoogleIdentity> {
  const existing = (window as unknown as { google?: GoogleIdentity }).google;
  if (existing?.accounts?.id) return Promise.resolve(existing);
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => resolve((window as unknown as { google: GoogleIdentity }).google);
    script.onerror = () => reject(new Error("Google sign-in failed to load"));
    document.head.appendChild(script);
  });
}

export function LoginPage({
  apiUrl,
  token,
  tokenResolved,
  currentEmail,
  onLoggedIn,
  onLogOut,
}: LoginPageProps) {
  usePageMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: "/login" });

  // undefined while /api/login/config is in flight, null when Google login
  // isn't configured on this deployment.
  const [googleClientId, setGoogleClientId] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(
    () => new URLSearchParams(window.location.search).has("code"),
  );
  // A login waiting on the person to confirm switching accounts, and the
  // request to resend, with confirm_switch, if they do.
  const [pendingSwitch, setPendingSwitch] = useState<
    { path: string; body: object; switchAccount: SwitchAccount } | null
  >(null);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  // The callback Google holds onto is registered once; read the latest token
  // through a ref rather than the value it closed over.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const finish = async (path: string, body: object) => {
    setError("");
    setPendingSwitch(null);
    try {
      // The response sets the token cookie to the account the login opened.
      const response = await fetch(`${apiUrl}${path}`, {
        ...WITH_COOKIE,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, token: tokenRef.current || null }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 409 && data.detail?.switch_account) {
        setPendingSwitch({ path, body, switchAccount: data.detail.switch_account });
        return;
      }
      if (!response.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Login failed. Try again.");
        return;
      }
      onLoggedIn(data as LoginResult, path === "/api/login/google" ? "google" : "email");
    } catch {
      setError("Couldn't reach the server. Try again.");
    }
  };

  useEffect(() => {
    fetch(`${apiUrl}/api/login/config`)
      .then((res) => res.json())
      .then((data) => setGoogleClientId(data.google_client_id || null))
      .catch(() => setGoogleClientId(null));
  }, [apiUrl]);

  // A magic link lands here as /login?code=... The code is single-use, so it
  // is spent exactly once, and dropped from the address bar first so a
  // reload or the back button can't try to spend it again.
  const spentRef = useRef(false);
  useEffect(() => {
    if (!tokenResolved || spentRef.current) return;
    const code = new URLSearchParams(window.location.search).get("code");
    if (!code) return;
    spentRef.current = true;
    window.history.replaceState({}, "", "/login");
    finish("/api/login/email/verify", { code }).finally(() => setVerifying(false));
  }, [tokenResolved]);

  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) return;
    let cancelled = false;
    loadGoogleIdentity()
      .then((google) => {
        if (cancelled || !googleButtonRef.current) return;
        google.accounts.id.initialize({
          client_id: googleClientId,
          callback: ({ credential }) => finish("/api/login/google", { credential }),
        });
        google.accounts.id.renderButton(googleButtonRef.current, {
          theme: "outline",
          size: "large",
          text: "continue_with",
          width: 280,
        });
      })
      .catch(() => !cancelled && setError("Google sign-in couldn't load. Try the email link instead."));
    return () => {
      cancelled = true;
    };
  }, [googleClientId, verifying, pendingSwitch]);

  if (verifying) {
    return (
      <article className="login-page">
        <h1>Logging you in...</h1>
      </article>
    );
  }

  if (pendingSwitch) {
    const { path, body, switchAccount } = pendingSwitch;
    const from = switchAccount.from_email || "another account";
    return (
      <article className="login-page">
        <h1>Switch accounts?</h1>
        <p className="login-switch-warning" role="alert">
          This browser is logged in as <strong>{from}</strong>.{" "}
          {switchAccount.new_account ? (
            <>
              <strong>{switchAccount.to_email}</strong> doesn't have an account
              yet, so continuing starts a new, empty one.
            </>
          ) : (
            <>
              Continuing opens the separate account for{" "}
              <strong>{switchAccount.to_email}</strong>.
            </>
          )}{" "}
          Designs saved to {from} stay there; log in with that address to get
          back to them.
        </p>
        {error && <p className="login-error" role="alert">{error}</p>}
        <div className="login-switch-actions">
          <button type="button" onClick={() => finish(path, { ...body, confirm_switch: true })}>
            Continue as {switchAccount.to_email}
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setPendingSwitch(null);
              setError("");
            }}
          >
            Stay as {from}
          </button>
        </div>
      </article>
    );
  }

  if (currentEmail) {
    return (
      <article className="login-page">
        <h1>Account</h1>
        <p className="login-current">
          You're logged in as <strong>{currentEmail}</strong>.
        </p>
        <button type="button" className="secondary" onClick={onLogOut}>
          Log out
        </button>
      </article>
    );
  }

  return (
    <article className="login-page">
      <h1>Log In</h1>
      <p>Log in with Google or a magic link to save your designs.</p>
      {error && <p className="login-error" role="alert">{error}</p>}

      {googleClientId && (
        <section>
          <div ref={googleButtonRef} className="google-button" />
        </section>
      )}
      {googleClientId && <p className="login-divider"><span>or</span></p>}

      <section>
        <EmailLinkForm apiUrl={apiUrl} submitLabel="Email me a sign-in link" />
      </section>

      <small className="login-note">
        Without an account, designs are saved locally in this browser.
      </small>
    </article>
  );
}
